/**
 * The one filter engine (FR-RPT-01, WD-06).
 *
 * One engine serves reports, the transaction list and bulk reclassify. Building it once
 * is the whole point — a second filter implementation is how two screens start disagreeing
 * about what "this month, business only" means.
 *
 * SD-08 governs the split: the period is always the anchor, **exactly one** further filter
 * goes to Firestore, and everything else is applied here in memory. A composite index is
 * needed per query *shape* and Firestore caps them at 200, so indexing every combination
 * of ten axes is not possible. The candidate set is small by construction — a month is
 * ~167 documents, a year ~2,000 — which is what makes in-memory refinement cheap.
 */

import type { AccountType } from './accounts';
import type { Paise } from './money';
import { isWithin } from './dates';
import { periodRange, type Period, type PeriodOptions } from './period';
import { countsTowardExpenses, countsTowardIncome, type Transaction, type TransactionType } from './transactions';

export interface Filters {
  period: Period;
  accountId?: string;
  type?: TransactionType;
  categoryId?: string;
  subcategoryId?: string;
  tagId?: string;
  isBusiness?: boolean;
  currency?: 'INR' | 'USD';
  minPaise?: Paise;
  maxPaise?: Paise;
  search?: string;
  /**
   * The follow-up worklist (FR-TXN-20): rows imported with an issue, or quick-captured
   * and sparse. Client-side only — it is the least selective filter there is, and the
   * period has already bounded the set.
   */
  needsEnrichment?: boolean;
}

export const EMPTY_FILTERS = (period: Period): Filters => ({ period });

/**
 * Which single filter is worth sending to Firestore.
 *
 * Ordered by selectivity: an account or category narrows far more than a business flag,
 * and only one can go. Everything not chosen here is applied client-side, so this is a
 * performance choice rather than a correctness one.
 */
export function serverFilter(
  filters: Filters,
): { field: 'accountId' | 'categoryId' | 'type'; value: string } | null {
  if (filters.accountId) return { field: 'accountId', value: filters.accountId };
  if (filters.categoryId) return { field: 'categoryId', value: filters.categoryId };
  if (filters.type) return { field: 'type', value: filters.type };
  return null;
}

/** Case-insensitive substring match across the free-text fields (AD-07). */
function matchesSearch(txn: Transaction, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [txn.description, txn.payee, txn.notes, txn.referenceNo].some(
    (value) => value?.toLowerCase().includes(needle),
  );
}

/**
 * Apply every filter the server did not.
 *
 * Deliberately re-checks the ones that *did* go server-side too. It costs nothing on an
 * already-narrowed set, and it means this function alone defines what a filter means —
 * so a bug in `serverFilter` shows up as slowness, never as a wrong answer.
 */
export function matchesFilters(
  txn: Transaction,
  filters: Filters,
  options: PeriodOptions = {},
): boolean {
  if (txn.deleted) return false;

  // The period is checked here as well as in the query. A custom range (FR-RPT-01) is
  // served by a query on the months it spans, so the first and last months come back
  // whole and have to be trimmed to the actual dates — without this, a range of
  // 1–15 August would export the whole of August and the export would not match the
  // screen, which is exactly what FR-RPT-09 forbids.
  if (!isWithin(txn.dateKey, periodRange(filters.period, options))) return false;
  if (filters.accountId &&
      txn.accountId !== filters.accountId &&
      txn.toAccountId !== filters.accountId) return false;
  if (filters.type && txn.type !== filters.type) return false;
  if (filters.categoryId && txn.categoryId !== filters.categoryId) return false;
  if (filters.subcategoryId && txn.subcategoryId !== filters.subcategoryId) return false;
  if (filters.tagId && !txn.tagIds.includes(filters.tagId)) return false;
  if (filters.isBusiness !== undefined && txn.isBusiness !== filters.isBusiness) return false;
  if (filters.currency && txn.currency !== filters.currency) return false;
  if (filters.minPaise !== undefined && txn.amountInrPaise < filters.minPaise) return false;
  if (filters.maxPaise !== undefined && txn.amountInrPaise > filters.maxPaise) return false;
  if (filters.needsEnrichment !== undefined && txn.needsEnrichment !== filters.needsEnrichment) {
    return false;
  }
  if (filters.search && !matchesSearch(txn, filters.search)) return false;
  return true;
}

export function applyFilters(
  transactions: Transaction[],
  filters: Filters,
  options: PeriodOptions = {},
): Transaction[] {
  return transactions.filter((txn) => matchesFilters(txn, filters, options));
}

// ---------------------------------------------------------------------------
// Chips and totals
// ---------------------------------------------------------------------------

export interface FilterChip {
  key: keyof Filters;
  label: string;
}

/**
 * Active filters, for display beneath the bar.
 *
 * WD-06: what is applied has to be visible without opening anything, so an export that
 * "looks wrong" is explained by looking at the screen.
 */
export function activeChips(
  filters: Filters,
  labels: {
    account?: (id: string) => string;
    category?: (id: string) => string;
    tag?: (id: string) => string;
    money?: (paise: Paise) => string;
  } = {},
): FilterChip[] {
  const chips: FilterChip[] = [];
  const name = (fn: ((id: string) => string) | undefined, id: string) => fn?.(id) ?? id;

  if (filters.accountId) chips.push({ key: 'accountId', label: name(labels.account, filters.accountId) });
  if (filters.type) chips.push({ key: 'type', label: filters.type });
  if (filters.categoryId) chips.push({ key: 'categoryId', label: name(labels.category, filters.categoryId) });
  if (filters.subcategoryId) chips.push({ key: 'subcategoryId', label: name(labels.category, filters.subcategoryId) });
  if (filters.tagId) chips.push({ key: 'tagId', label: `#${name(labels.tag, filters.tagId)}` });
  if (filters.isBusiness !== undefined) {
    chips.push({ key: 'isBusiness', label: filters.isBusiness ? 'Business' : 'Personal' });
  }
  if (filters.currency) chips.push({ key: 'currency', label: filters.currency });
  if (filters.needsEnrichment !== undefined) {
    chips.push({
      key: 'needsEnrichment',
      label: filters.needsEnrichment ? 'Needs follow-up' : 'Already reviewed',
    });
  }
  if (filters.minPaise !== undefined) {
    chips.push({ key: 'minPaise', label: `≥ ${labels.money?.(filters.minPaise) ?? filters.minPaise}` });
  }
  if (filters.maxPaise !== undefined) {
    chips.push({ key: 'maxPaise', label: `≤ ${labels.money?.(filters.maxPaise) ?? filters.maxPaise}` });
  }
  if (filters.search) chips.push({ key: 'search', label: `“${filters.search}”` });
  return chips;
}

export function clearChip(filters: Filters, key: keyof Filters): Filters {
  const next = { ...filters };
  delete next[key];
  return next;
}

export function hasActiveFilters(filters: Filters): boolean {
  return activeChips(filters).length > 0;
}

export interface Totals {
  incomePaise: Paise;
  expensePaise: Paise;
  transferPaise: Paise;
  netPaise: Paise;
  count: number;
}

/**
 * Totals for a filtered set.
 *
 * Transfers are counted separately and appear in neither income nor expense (FR-TXN-05),
 * so the footer of a filtered list doubles as an honest answer to "what did I spend on
 * this" without a card payment inflating it.
 */
export function totalsFor(
  transactions: Transaction[],
  accountType: (accountId: string) => AccountType | undefined,
): Totals {
  let incomePaise = 0;
  let expensePaise = 0;
  let transferPaise = 0;

  for (const txn of transactions) {
    if (countsTowardIncome(txn)) incomePaise += txn.amountInrPaise;
    else if (countsTowardExpenses(txn, accountType)) expensePaise += txn.amountInrPaise;
    else if (txn.type === 'transfer') transferPaise += txn.amountInrPaise;
  }

  return {
    incomePaise,
    expensePaise,
    transferPaise,
    netPaise: incomePaise - expensePaise,
    count: transactions.length,
  };
}
